import { useCallback, useEffect, useState } from "react";
import { manufacturersApi, type ManufacturerView } from "../api/assets";
import type { ApiError } from "../api/client";

/**
 * The manufacturer codebook (`GET /manufacturers`, sprint 5g) shared by every screen that
 * picks one — the asset form, the asset card. Same shape as `usePositionKinds`: fetched once
 * per session, cached at module level; a write (the codebook page, "Novi proizvođač" on the
 * asset form) calls `refresh()` so every open picker sees it.
 */

let cache: ManufacturerView[] | null = null;
let inflight: Promise<ManufacturerView[]> | null = null;
const listeners = new Set<() => void>();

function load(force = false): Promise<ManufacturerView[]> {
  if (!force && cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = manufacturersApi
    .list()
    .then((list) => {
      cache = list;
      inflight = null;
      for (const l of listeners) l();
      return list;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Test hook: drop the module cache so the next mount fetches again. */
export function resetManufacturersCache(): void {
  cache = null;
  inflight = null;
}

export type ManufacturersHook = {
  /** Sorted by name (as the server returns them). Empty while loading or on error. */
  manufacturers: ManufacturerView[];
  loading: boolean;
  error: string | null;
  /** Re-fetch after a write; every mounted hook re-renders. */
  refresh: () => Promise<ManufacturerView[]>;
};

export function useManufacturers(): ManufacturersHook {
  const [manufacturers, setManufacturers] = useState<ManufacturerView[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sync = () => { if (!cancelled && cache) setManufacturers(cache); };
    listeners.add(sync);
    load()
      .then(() => { if (!cancelled) { sync(); setLoading(false); } })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; listeners.delete(sync); };
  }, []);

  const refresh = useCallback(() => load(true), []);
  return { manufacturers, loading, error, refresh };
}
